import { useEffect, useRef, useState } from "react";
import type { FileDTO } from "../../shared/types";
import { get, uploadFile } from "../lib/api";
import { fileUrl, formatBytes, iconFor } from "./Files";
import { Icon } from "./Icon";
import { List, ListRow } from "./List";
import { TextLink } from "./TextLink";
import { Button, ErrorText, timeAgo } from "./ui";

/**
 * The files of an agent — its own, and those its owners keep for every run of it — or of a scope (every agent working
 * under it reads them). An admin sees each one's details; it opens only for whoever it is shared with (security plan D16).
 */
export type KeptOn = { agentId: string } | { subjectId: string };

/**
 * `canUpload` is false where the server would refuse the upload: what an agent keeps for every run is its owners' to add
 * (D5, D16), so an admin who does not own it reads the list and is told why there is no button.
 */
export function KeptFiles({ on, title = "Files", hint, canUpload = true }: { on: KeptOn; title?: string; hint: string; canUpload?: boolean }) {
  const picker = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileDTO[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fields: Record<string, string> = "agentId" in on ? { agentId: on.agentId } : { subjectId: on.subjectId };
  const query = new URLSearchParams({ ...fields, kept: "1", limit: "1000" }).toString();

  const load = () => get<FileDTO[]>(`/api/files?${query}`).then(setFiles, (e) => setError(e.message));
  useEffect(() => {
    void load();
  }, [query]);

  const upload = async (picked: FileList | null) => {
    if (!picked?.length) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(picked)) await uploadFile<FileDTO>(file, undefined, fields);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <List
      title={files ? `${title} · ${files.length}` : title}
      hint={hint}
      action={
        canUpload && (
          <>
            <input
              ref={picker}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                void upload(e.target.files);
                e.target.value = "";
              }}
            />
            <Button disabled={busy} onClick={() => picker.current?.click()}>{busy ? "Uploading…" : "Upload a file"}</Button>
          </>
        )
      }
      empty={files?.length === 0 ? "No files yet." : undefined}
      footer={
        (error || !canUpload) && (
          <>
            {!canUpload && <p className="text-xs text-muted">Its owners add files here. To add one, an admin first adds themselves as an owner in Settings.</p>}
            <ErrorText error={error} />
          </>
        )
      }
    >
      {files?.map((f) => (
        <ListRow
          key={f.id}
          leading={
            <span className="grid size-8 place-items-center rounded-lg bg-fill text-muted">
              <Icon name={iconFor(f.mime)} className="size-4" />
            </span>
          }
          title={<span className="truncate text-sm font-medium">{f.name}</span>}
          subtitle={
            <span className="text-xs">
              {formatBytes(f.size)} · {f.source === "agent" ? "made by an agent" : `uploaded by ${f.ownerName ?? "someone"}`} {timeAgo(f.createdAt)}
            </span>
          }
          trailing={f.canOpen ? <TextLink href={fileUrl(f.id)}>Download</TextLink> : <span className="text-xs text-faint">not shared with you</span>}
        />
      ))}
    </List>
  );
}
